'use client';
import * as React from 'react';
import { cn } from '../cn';

export interface SegmentedOption<T extends string = string> {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
  count?: number;
}
export interface SegmentedControlProps<T extends string = string> {
  value: T;
  onValueChange: (value: T) => void;
  options: SegmentedOption<T>[];
  size?: 'sm' | 'md';
  className?: string;
  'aria-label'?: string;
}

/** iOS-style segmented control with roving keyboard focus. */
export function SegmentedControl<T extends string = string>({ value, onValueChange, options, size = 'md', className, ...aria }: SegmentedControlProps<T>) {
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const idx = Math.max(0, options.findIndex((o) => o.value === value));
  const onKey = (e: React.KeyboardEvent, i: number) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + options.length) % options.length;
    refs.current[next]?.focus();
    onValueChange(options[next]!.value);
  };
  return (
    <div role="tablist" aria-label={aria['aria-label']} className={cn('relative inline-grid rounded-[var(--radius-sm)] bg-fill-2 p-0.5', size === 'sm' ? 'h-7' : 'h-8', className)} style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
      <div aria-hidden className="absolute top-0.5 bottom-0.5 rounded-[6px] bg-surface shadow-[0_1px_3px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.04)] transition-[left] duration-200 ease-[var(--ease-spring)]" style={{ left: `calc(${(idx / options.length) * 100}% + 2px)`, width: `calc(${100 / options.length}% - 4px)` }} />
      {options.map((o, i) => (
        <button
          key={o.value}
          ref={(el) => {
            refs.current[i] = el;
          }}
          role="tab"
          type="button"
          aria-selected={o.value === value}
          tabIndex={o.value === value ? 0 : -1}
          onClick={() => onValueChange(o.value)}
          onKeyDown={(e) => onKey(e, i)}
          className={cn('relative z-10 inline-flex items-center justify-center gap-1.5 px-3 rounded-[6px] font-medium transition-colors outline-none', size === 'sm' ? 'text-[12px]' : 'text-[13px]', o.value === value ? 'text-text' : 'text-text-2 hover:text-text')}
        >
          {o.icon ? <span className="[&>svg]:size-3.5 inline-flex">{o.icon}</span> : null}
          {o.label}
          {o.count !== undefined ? <span className={cn('tabular text-[11px] rounded-full px-1.5 min-w-[18px] text-center', o.value === value ? 'bg-fill-2 text-text-2' : 'bg-transparent text-text-3')}>{o.count}</span> : null}
        </button>
      ))}
    </div>
  );
}
